<template>
    <div class="ix-dashboard-block bordered">
        <div class="d-flex align-items-center justify-content-between flex-wrap" style="gap: 10px;">
            <h2 class="ix-page-title-secondary mb-0">Suivi de consommation par article</h2>
            <div style="min-width: 420px; flex-grow: 1; max-width: 520px;">
                <multiselect
                    v-model="selectedArticle"
                    :options="articleOptions"
                    track-by="id"
                    label="name"
                    :searchable="true"
                    :multiple="false"
                    :loading="isSearching"
                    :showNoResults="false"
                    placeholder="Sélectionner un article"
                    selectLabel=""
                    selectedLabel="Sélectionné"
                    deselectLabel=""
                    @search-change="searchArticles"
                    @input="onSelectArticle">
                    <template slot="noOptions">Aucun résultat.</template>
                </multiselect>
            </div>
        </div>

        <template v-if="!selectedArticle">
            <p class="text-muted mt-3 mb-0">Sélectionnez un article pour afficher la synthèse des affaires par quantité imputée.</p>
        </template>

        <template v-else>
            <div class="row mt-3">
                <div class="col-sm-12 col-md-3">
                    <div class="ix-count-box">
                        <h2 class="ix-count-box-title">Affaires concernées</h2>
                        <span class="ix-count-box-value">{{ loading ? '-' : totals.deals_count }}</span>
                    </div>
                </div>
                <div class="col-sm-12 col-md-3">
                    <div class="ix-count-box">
                        <h2 class="ix-count-box-title">Quantité prévisionnelle totale</h2>
                        <span class="ix-count-box-value">{{ loading ? '-' : formatQuantity(totals.predicted_quantity) }}</span>
                    </div>
                </div>
                <div class="col-sm-12 col-md-3">
                    <div class="ix-count-box">
                        <h2 class="ix-count-box-title">Quantité imputée totale</h2>
                        <span class="ix-count-box-value">{{ loading ? '-' : formatQuantity(totals.imputed_quantity) }}</span>
                    </div>
                </div>
                <div class="col-sm-12 col-md-3">
                    <div class="ix-count-box">
                        <h2 class="ix-count-box-title">Delta global</h2>
                        <span class="ix-count-box-value" :style="loading ? {} : deltaStyle(totals.delta)">
                            {{ loading ? '-' : formatDelta(totals.delta) }}
                        </span>
                    </div>
                </div>
            </div>

            <div class="ix-table-box mt-3">
                <table class="ix-table ix-table-sm">
                    <thead>
                        <tr>
                            <th>Affaire</th>
                            <th class="text-center">Quantité prévisionnelle</th>
                            <th class="text-center">
                                Quantité imputée
                                <i class="far fa-info-circle" data-toggle="tooltip" data-container="body" data-placement="bottom" data-boundary="window" title="(Quantité réceptionnée via réception fournisseur + Quantité des bons de sortie de stock) - Quantité des bons d'entrée de stock"></i>
                            </th>
                            <th class="text-center">Nombre de mouvements</th>
                            <th class="text-right">Delta</th>
                        </tr>
                    </thead>
                    <tbody v-if="loading">
                        <tr>
                            <td colspan="5" class="text-center"><i class="far fa-circle-notch fa-spin"></i></td>
                        </tr>
                    </tbody>
                    <tbody v-else-if="deals.length === 0">
                        <tr>
                            <td colspan="5" class="text-center text-muted">Aucune affaire trouvée pour cet article</td>
                        </tr>
                    </tbody>
                    <tbody v-else>
                        <tr v-for="deal in deals" :key="deal.id">
                            <td>
                                <a :href="deal.url" class="ix-link">{{ deal.number }}</a>
                                <span class="d-block text-muted" style="font-size: 12px;">{{ deal.name }}</span>
                                <span class="d-block text-muted" style="font-size: 12px;">
                                    <span class="ix-state" :class="deal.state_color">{{ deal.state_label }}</span>
                                    {{ deal.created_at }}
                                </span>
                            </td>
                            <td class="text-center">{{ formatQuantity(deal.predicted_quantity) }}</td>
                            <td class="text-center">{{ formatQuantity(deal.imputed_quantity) }}</td>
                            <td class="text-center">{{ deal.movements_count }}</td>
                            <td class="text-right" :style="deltaStyle(deal.delta)">{{ formatDelta(deal.delta) }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <p class="text-muted mt-2 mb-0" style="font-size: 12px;">
                Delta = quantité imputée &minus; quantité prévisionnelle &middot; affaires triées de la plus récente à la plus ancienne
            </p>
        </template>
    </div>
</template>

<script>
import $ from "jquery";
import Multiselect from 'vue-multiselect'

export default {
    components: { Multiselect },
    props: {
        routeFavoriteArticleDeals: String,
        routeArticlesAutocomplete: String,
        initialArticle: Object,
    },
    data() {
        return {
            selectedArticle: this.initialArticle || null,
            articleOptions: [],
            isSearching: false,
            loading: false,
            totals: { deals_count: 0, predicted_quantity: 0, imputed_quantity: 0, delta: 0 },
            deals: [],
        }
    },
    methods: {
        formatQuantity(value) {
            return Number(value).toLocaleString('fr-FR', { maximumFractionDigits: 3 });
        },
        formatDelta(value) {
            const formatted = this.formatQuantity(Math.abs(value));
            return (value > 0 ? '+' : (value < 0 ? '-' : '')) + formatted;
        },
        deltaStyle(value) {
            if (value > 0) return { color: 'var(--ix-red)' };
            if (value < 0) return { color: 'var(--ix-green)' };
            return {};
        },
        async searchArticles(query) {
            this.isSearching = true;
            await fetch(this.routeArticlesAutocomplete + '?q=' + encodeURIComponent(query), {
                method: 'GET',
                ...this.getHttpJsonRequestHeaders()
            }).then((response) => {
                return response.json();
            }).then((data) => {
                this.articleOptions = data;
            }).catch(() => {
                this.articleOptions = [];
            });
            this.isSearching = false;
        },
        onSelectArticle() {
            this.fetchDeals();
        },
        async fetchDeals() {
            if (!this.selectedArticle) {
                this.deals = [];
                this.totals = { deals_count: 0, predicted_quantity: 0, imputed_quantity: 0, delta: 0 };
                return;
            }
            this.loading = true;
            await fetch(this.routeFavoriteArticleDeals + '?article_id=' + this.selectedArticle.id, {
                method: 'GET',
                ...this.getHttpJsonRequestHeaders()
            }).then((response) => {
                return response.json();
            }).then((data) => {
                this.deals = data.deals;
                this.totals = data.totals;
            }).catch(() => {
                this.deals = [];
                this.totals = { deals_count: 0, predicted_quantity: 0, imputed_quantity: 0, delta: 0 };
            });
            this.loading = false;
            this.$nextTick(() => {
                $('[data-toggle="tooltip"]:not(.copy-button)').tooltip();
            });
        },
    },
    mounted() {
        if (this.selectedArticle) {
            this.fetchDeals();
        }
    }
}
</script>
